import React from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Breadcrumbs, 
  Link, 
  Divider, 
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from "@mui/material";
import FooterSection from "../home/FooterSection";

// Custom bullet point component
const BulletPoint = () => (
  <Box 
    component="span" 
    sx={{ 
      display: "inline-block",
      width: 6,
      height: 6,
      borderRadius: "50%",
      backgroundColor: "#0f84fe",
      marginRight: 1.5,
      flexShrink: 0,
      mt: 0.8
    }} 
  />
);

const RefundPolicy = () => {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* <NavigationSection /> */}
      
      {/* Header Banner */}
      <Box
        sx={{
          bgcolor: "#232323",
          color: "white",
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 }
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 600,
              mb: 2
            }}
          >
            Refund Policy
          </Typography>
          
          <Breadcrumbs 
            aria-label="breadcrumb" 
            sx={{ 
              color: "#8d8d8b",
              "& .MuiBreadcrumbs-separator": {
                mx: 1
              }
            }}
          >
            <Link 
              underline="hover" 
              color="inherit" 
              href="/"
              sx={{ 
                "&:hover": { color: "#0f84fe" },
                transition: "color 0.2s"
              }}
            >
              Home
            </Link>
            <Typography color="#8d8d8b">Refund Policy</Typography>
          </Breadcrumbs>
        </Container>
      </Box>
      
      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 5 },
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
            borderRadius: 2
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#0f84fe",
              fontWeight: 600,
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: 1
            }}
          >
            Last Updated: May 19, 2025
          </Typography>
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Introduction
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            Alfa 2020 s.r.o. ("we", "our", or "us") is committed to customer satisfaction. This Refund Policy 
            outlines the terms and conditions for refunds related to our remote technical support, software installation, 
            network setup and troubleshooting services.
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            Please read this Refund Policy carefully before purchasing or using our services. By accessing or using 
            our services, you agree to be bound by the terms and conditions outlined in this policy.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Service Satisfaction Guarantee
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            We stand behind the quality of our technical support services and are committed to resolving your 
            technical issues to your satisfaction. If you are not satisfied with the service provided, please 
            contact our customer support team immediately to allow us the opportunity to address your concerns.
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              One-Time Services
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              For one-time technical support services, software installations, or network troubleshooting:
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="If we are unable to resolve your technical issue as described in the service agreement, we will issue a full refund." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Refund requests must be submitted within 7 days of the service completion date." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="If you cancel a scheduled appointment less than 24 hours before the appointment time, a cancellation fee of 25% may apply." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Subscription Services
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              For monthly or annual technical support subscription plans:
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="You may cancel your subscription at any time through your account dashboard or by contacting our customer support team." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="For monthly subscriptions, cancellations will take effect at the end of the current billing cycle. No partial refunds will be issued for the remainder of the month." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="For annual subscriptions, if you cancel within 30 days of purchase, you will receive a full refund. After 30 days, you may receive a prorated refund for the unused portion of your subscription, less a $25 administrative fee." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="If you have used any of the included service hours or sessions in your subscription plan, these will be deducted from your refund amount at their standard non-subscription rates." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Software Products
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              For software products purchased through our service:
            </Typography>
            
            <List sx={{ pl: 2 }}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Refunds for third-party software are subject to the manufacturer's refund policy." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="For our proprietary software products, we offer a 30-day money-back guarantee if the software does not function as described." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                  <BulletPoint />
                </ListItemIcon>
                <ListItemText 
                  primary="Refunds will not be issued if the software incompatibility is due to your system not meeting the minimum requirements specified in the product description." 
                  sx={{ 
                    m: 0,
                    "& .MuiTypography-root": { 
                      color: "#444", 
                      lineHeight: 1.7 
                    } 
                  }} 
                />
              </ListItem>
            </List>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Non-Refundable Services and Exceptions
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            The following services and situations are generally not eligible for refunds:
          </Typography>
          
          <List sx={{ pl: 2, mb: 4 }}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Services that have been fully performed and completed to the specifications agreed upon" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Emergency or after-hours premium services" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Software activation keys or digital licenses that have been delivered and activated" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Issues resulting from your failure to follow our technicians' advice or recommendations" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Problems caused by subsequent changes to your system after our service has been completed" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            However, we evaluate each situation on a case-by-case basis and may make exceptions at our discretion.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            How to Request a Refund
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            To request a refund, please follow these steps:
          </Typography>
          
          <List sx={{ pl: 2, mb: 4 }}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Contact our customer support team at refunds@alfagroup-24.com" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Provide your order number, service date, and a detailed explanation of why you are requesting a refund." 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Our team will review your request and respond within 2 business days." 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="If approved, refunds will be processed to the original payment method within 5-10 business days." 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Service Credits
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            In some cases, instead of a monetary refund, we may offer service credits that can be applied to future 
            technical support services. Service credits:
          </Typography>
          
          <List sx={{ pl: 2, mb: 4 }}>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Are valid for 12 months from the date of issue" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="Cannot be transferred or exchanged for cash" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.8 }}>
                <BulletPoint />
              </ListItemIcon>
              <ListItemText 
                primary="May be used for any service we offer, including one-time services or subscription plans" 
                sx={{ 
                  m: 0,
                  "& .MuiTypography-root": { 
                    color: "#444", 
                    lineHeight: 1.7 
                  } 
                }} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Payment Method Refund Policies
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 3, lineHeight: 1.7 }}>
            Refund processing times may vary depending on your payment method:
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Credit/Debit Cards
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              Credit card refunds typically take 5-10 business days to appear on your statement, depending on your 
              card issuer's policies.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              PayPal
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              PayPal refunds are usually processed within 3-5 business days.
            </Typography>
          </Box>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" component="h3" sx={{ fontSize: "1.3rem", fontWeight: 600, mb: 2 }}>
              Bank Transfers
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              Bank transfers may take 7-14 business days to process due to banking system procedures.
            </Typography>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Changes to This Refund Policy
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 4, lineHeight: 1.7 }}>
            We may update our Refund Policy from time to time. We will notify you of any changes by posting the new 
            Refund Policy on this page and updating the "Last Updated" date at the top of this policy. You are advised 
            to review this Refund Policy periodically for any changes. Changes to this Refund Policy are effective when 
            they are posted on this page.
          </Typography>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h2" component="h2" sx={{ fontSize: "1.75rem", fontWeight: 600, mb: 3 }}>
            Contact Us
          </Typography>
          
          <Typography paragraph sx={{ color: "#444", mb: 2, lineHeight: 1.7 }}>
            If you have any questions about this Refund Policy, please contact us:
          </Typography>
          
          <Box sx={{ ml: 2, mb: 4 }}>
            <Typography paragraph sx={{ color: "#444", mb: 1, lineHeight: 1.7 }}>
              <strong>By email:</strong> refunds@alfagroup-24.com
            </Typography>
            
            <Typography paragraph sx={{ color: "#444", lineHeight: 1.7 }}>
              <strong>By mail:</strong> Alfa 2020 s.r.o.<br />
             Veľká Okružná 17 
                Žilina 010 01
            </Typography>
          </Box>
        </Paper>
      </Container>
      
      <FooterSection />
    </Box>
  );
};

export default RefundPolicy;